import * as joda from "@js-joda/core";
import { connection } from "../../../connection";
import { get_holidays } from "../../../sql/classy/periods";
import { periodid } from "../../../sql/classy/types";
import { promiseAllFromObject } from "../../lib/promise";
import { dateInDaterange, isContained } from "../../lib/time";

export type period = {
  id: periodid;
  firstday: joda.LocalDate;
  lastday: joda.LocalDate;
  earliestpublicplanningdate: joda.LocalDate;
  openpublicenrollments: joda.LocalDate | null;
  closepublicenrollments: joda.LocalDate;
};

export type holiday = {
  description: string;
  periodid: periodid;
  firstday: joda.LocalDate;
  lastday: joda.LocalDate;
};

export function getCurrentOrNextPeriod(
  today: joda.LocalDate,
  allPeriods: period[],
): period | null {
  const todayEpochDay = today.toEpochDay();
  const inperiod = allPeriods.find(
    (p) =>
      p.firstday.toEpochDay() <= todayEpochDay &&
      todayEpochDay <= p.lastday.toEpochDay(),
  );
  if (inperiod) {
    return inperiod;
  } else {
    const nextperiod = allPeriods
      .orderBy((p) => p.firstday.toEpochDay())
      .find((p) => p.firstday.toEpochDay() > todayEpochDay);
    return nextperiod || null;
  }
}

export function getNextPeriod<Period extends period>(
  allPeriods: Period[],
  id: periodid,
): Period | null {
  const ordered = allPeriods.orderBy((p) => p.firstday.toEpochDay());
  const index = ordered.findIndex((p) => p.id === id);
  if (index < 0) {
    return null;
  }
  return ordered[index + 1] || null;
}

export function getPreviousPeriod<Period extends period>(
  allPeriods: Period[],
  id: periodid,
): Period | null {
  const ordered = allPeriods.orderBy((p) => p.firstday.toEpochDay());
  const index = ordered.findIndex((p) => p.id === id);
  if (index < 0) {
    return null;
  }
  return ordered[index - 1] || null;
}

export async function getValidFirstDayForPublic(
  db: connection,
  allPeriods: period[],
  current: joda.LocalDate,
): Promise<[joda.LocalDate, period, holiday[]] | null> {
  // We beginnen bij huidige / volgende periode
  const ordered = allPeriods.orderBy((p) => p.firstday.toEpochDay(), "asc");
  const period =
    ordered.find(
      (p) =>
        // Zoek de periode waar we IN ziten (qua earliest/latest)
        (p.earliestpublicplanningdate.isEqual(current) ||
          p.earliestpublicplanningdate.isBefore(current)) &&
        p.closepublicenrollments.isAfter(current),
    ) ||
    // Zoek eerstvolgende period
    ordered.find((p) => p.earliestpublicplanningdate.isAfter(current));
  if (!period) {
    return null;
  }

  if (period.earliestpublicplanningdate.isAfter(current)) {
    // Spring naar earliest public planning date (week)
    // van deze periode als we daar nog niet zijn
    return getValidFirstDayForPublic(
      db,
      allPeriods,
      period.earliestpublicplanningdate,
    );
  }
  if (period.closepublicenrollments.isBefore(current)) {
    //
    const nextPeriod = getNextPeriod(allPeriods, period.id);
    if (nextPeriod) {
      return getValidFirstDayForPublic(
        db,
        allPeriods,
        nextPeriod.earliestpublicplanningdate,
      );
    }
  }

  const holidays = await get_holidays(db, { period_id: period.id });

  const h = holidays.find((h) => isContained(current, h));
  // Als we in een holiday zitten gaan we naar het einde
  // Zo springen we over volledige weken die holidays zijn, maar niet over enkele-dagen holidays
  if (h) {
    return getValidFirstDayForPublic(db, allPeriods, h.lastday.plusDays(1));
  } else {
    return [current, period, holidays];
  }
}

export function calcPlanningDateKind(
  d: joda.LocalDate,
  p: period,
  hs: {
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
    description: string;
  }[],
):
  | { tag: "open" }
  | { tag: "holiday"; description: string }
  | { tag: "outsideperiod" } {
  if (!isContained(d, p)) {
    return { tag: "outsideperiod" };
  } else {
    const holiday = hs.find((h) => isContained(d, h));
    if (holiday) {
      return { tag: "holiday", description: holiday.description };
    } else {
      return { tag: "open" };
    }
  }
}

export async function getCurrentAndNextPeriodWithHolidays(
  db: connection,
  allPeriods: period[],
  date: joda.LocalDate,
): Promise<{
  period: period & { holidays: holiday[] };
  nextPeriod: null | (period & { holidays: holiday[] });
}> {
  const period = getCurrentOrNextPeriod(date, allPeriods);
  if (!period) {
    throw new Error("No period found");
  }

  const nextPeriod = getNextPeriod(allPeriods, period.id);

  const holidaysP = get_holidays(db, { period_id: period.id });

  const nextPeriodHolidaysP = nextPeriod
    ? get_holidays(db, { period_id: nextPeriod.id })
    : [];

  return promiseAllFromObject({
    holidays: holidaysP,
    nextPeriodHolidays: nextPeriodHolidaysP,
  }).then(({ holidays, nextPeriodHolidays }) => {
    return {
      period: { ...period, holidays },
      nextPeriod:
        nextPeriod === null
          ? null
          : { ...nextPeriod, holidays: nextPeriodHolidays },
    };
  });
}

export function goForwardXDaysInsidePeriods(opts: {
  currentDate: joda.LocalDate;
  numberOfDays: number;
  period: { firstday: joda.LocalDate; lastday: joda.LocalDate };
  nextPeriod: { firstday: joda.LocalDate; lastday: joda.LocalDate } | null;
}): joda.LocalDate | null {
  const nextDate_ = opts.currentDate.plusDays(opts.numberOfDays);
  const nextDateInPeriod = dateInDaterange(
    { date: nextDate_ },
    { from: opts.period.firstday, until: opts.period.lastday },
  );
  const nextDate = nextDateInPeriod
    ? nextDate_
    : opts.nextPeriod !== null
    ? opts.nextPeriod.firstday
    : null;
  return nextDate;
}

export function goBackXDaysInsidePeriods(opts: {
  currentDate: joda.LocalDate;
  numberOfDays: number;
  period: { firstday: joda.LocalDate; lastday: joda.LocalDate };
  previousPeriod: { firstday: joda.LocalDate; lastday: joda.LocalDate } | null;
}): joda.LocalDate | null {
  const prevDate_ = opts.currentDate.minusDays(opts.numberOfDays);
  const prevDateInPeriod = dateInDaterange(
    { date: prevDate_ },
    { from: opts.period.firstday, until: opts.period.lastday },
  );
  const prevDate = prevDateInPeriod
    ? prevDate_
    : opts.previousPeriod !== null
    ? opts.previousPeriod.lastday
    : null;
  return prevDate;
}
